import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue';
import { getHealthScoreStateFromScore } from '../../../../../../utils/healthScoreUtils';
import type { DealHealthScoreRootCauseDetailsState } from '../../../../../../utils/hooks/useDealHealthScoreRootCauseDetails';
import { useDealInsightsContext } from '../../../../../context/DealInsightsContext';
import { formatInventoryQualityScore } from '../../../../../utils/formatInventoryQualityScore';

type UseGeneralDataTableDataArgs = {
  getDealRootCauseDetails?: (dealId: string) => DealHealthScoreRootCauseDetailsState;
};

export const useGeneralDataTableData = ({ getDealRootCauseDetails }: UseGeneralDataTableDataArgs = {}) => {
  const { dealInsights } = useDealInsightsContext();
  const { data, chartsLoadingState } = dealInsights;

  const safeGetDealRootCauseDetails =
    getDealRootCauseDetails ??
    (() => ({
      aiAnalysis: null,
      currentScore: null,
      error: false,
      loading: false,
    }));

  const transformedData =
    data?.deal_stats.objects.map(item => {
      const rootCauseDetails = safeGetDealRootCauseDetails(String(item.ox_deal_id));
      return {
        // Raw values for coloring/visualization
        ...item,
        ox_deal_id: item.ox_deal_id,
        health_score: {
          healthState: getHealthScoreStateFromScore(item.health_score),
          aiAnalysis: rootCauseDetails.aiAnalysis,
          ...(rootCauseDetails.currentScore !== null ? { currentScore: rootCauseDetails.currentScore } : {}),
          error: rootCauseDetails.error,
          loading: rootCauseDetails.loading,
          score: item.health_score,
        },
        impressions_raw: item.impressions,
        spend_raw: item.spend,
        vcr_raw: item.vcr,
        ctr_raw: item.ctr,
        ecpm_raw: item.ecpm,
        bid_rate_raw: item.bid_rate,
        win_rate_raw: item.win_rate,
        viewability_raw: item.viewability,
        iqs_on_impression_raw: item.iqs_on_impression,
        // Formatted values for display
        impressions: abbreviateNumericValue(item.impressions, { allowZero: true, nullValue: 'N/A' }),
        spend: abbreviateNumericValue(item.spend, { allowZero: true, nullValue: 'N/A' }),
        vcr: abbreviateNumericValue(item.vcr, { allowZero: true, nullValue: 'N/A', multiplier: 100 }),
        ctr: abbreviateNumericValue(item.ctr, { allowZero: true, nullValue: 'N/A', multiplier: 100 }),
        ecpm: abbreviateNumericValue(item.ecpm, { allowZero: true, nullValue: 'N/A' }),
        bid_rate: abbreviateNumericValue(item.bid_rate, { allowZero: true, nullValue: 'N/A', multiplier: 100 }),
        win_rate: abbreviateNumericValue(item.win_rate, { allowZero: true, nullValue: 'N/A', multiplier: 100 }),
        viewability: abbreviateNumericValue(item.viewability, { allowZero: true, nullValue: 'N/A', multiplier: 100 }),
        iqs_on_impression: formatInventoryQualityScore(item.iqs_on_impression),
      };
    }) ?? [];

  return {
    data: transformedData,
    isLoading: !!chartsLoadingState.deal_stats,
  };
};
